import * as React from "react"

import * as PopoverPrimitive from "@radix-ui/react-popover"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../cn"

/* ── Variants ─────────────────────────────────────────────── */

const popoverContentVariants = cva([
  "z-50 rounded-md",
  "bg-surface-default text-coval-text",
  "border border-coval-border shadow-md",
  "p-3",
])

/* ── Root & Trigger ───────────────────────────────────────── */

const Popover = PopoverPrimitive.Root
const PopoverTrigger = PopoverPrimitive.Trigger
const PopoverClose = PopoverPrimitive.Close
const PopoverAnchor = PopoverPrimitive.Anchor

/* ── Content ──────────────────────────────────────────────── */

export type PopoverContentProps = React.ComponentPropsWithoutRef<
  typeof PopoverPrimitive.Content
> &
  VariantProps<typeof popoverContentVariants>

const PopoverContent = React.forwardRef<
  React.ComponentRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(({ className, sideOffset = 4, align = "start", ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      align={align}
      className={cn(popoverContentVariants(), className)}
      {...props}
    />
  </PopoverPrimitive.Portal>
))

PopoverContent.displayName = PopoverPrimitive.Content.displayName

/* ── Title ────────────────────────────────────────────────── */

const PopoverTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "px-2 py-1.5 text-xs font-medium text-coval-text-muted",
      className,
    )}
    {...props}
  />
))

PopoverTitle.displayName = "PopoverTitle"

/* ── Item ─────────────────────────────────────────────────── */

export interface PopoverItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  checked?: boolean
}

const PopoverItem = React.forwardRef<HTMLButtonElement, PopoverItemProps>(
  ({ className, checked, children, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      className={cn(
        "relative flex w-full cursor-pointer select-none items-center gap-2 rounded-sm py-1.5 px-2",
        "text-sm text-coval-text outline-none",
        "hover:bg-surface-default-hover disabled:hover:bg-transparent",
        "disabled:cursor-default disabled:opacity-50",
        className,
      )}
      {...props}
    >
      {children}
      {checked && (
        <span className="ml-auto flex h-3.5 w-3.5 shrink-0 items-center justify-center">
          <CheckIcon />
        </span>
      )}
    </button>
  ),
)

PopoverItem.displayName = "PopoverItem"

/* ── Inline icons ─────────────────────────────────────────── */

function CheckIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M2.5 6L5 8.5L9.5 3.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/* ── Exports ──────────────────────────────────────────────── */

export {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverClose,
  PopoverAnchor,
  PopoverTitle,
  PopoverItem,
  popoverContentVariants,
}
